import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { isPWSSessionBackend, parsePWSError, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import { FORM_ERRORS, SimpleInput, validatePasswordHintContains, validatePasswordsMatch } from "../../forms";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";
import { useCoastAlerts } from "../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../common/UsersButton.components";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("Coast Reset Password Component");

interface ResetPasswordInputs {
  confirmPassword: string;
  passwordHint?: string;
  password: string;
}

interface CoastResetPasswordProps {
  forgotPassword: () => void;
  passwordToken?: string;
  goBack: () => void;
  store?: Store;
}

export const CoastResetPassword = inject("store")(
  ({ forgotPassword, goBack, passwordToken, store }: CoastResetPasswordProps) => {
    const { errors, register, handleSubmit, watch } = useForm<ResetPasswordInputs>();
    const { alerts, setErrorAlert, setSuccessAlert, clearAllAlerts } = useCoastAlerts();
    const [isProcessing, setIsProcessing] = useState(false);

    const session = store?.session?.currentSession;

    if (!isPWSSessionBackend(session)) {
      return null;
    }

    if (!passwordToken) {
      return (
        <section className="component-reset-password">
          <CoastAlertDisplayError message={COAST_MESSAGES.INVALID_LINK} isDisplayed={true} />
          <UsersButton label="Forgot Password" click={forgotPassword}>
            Forgot Password
          </UsersButton>
        </section>
      );
    }

    const onSubmit: SubmitHandler<ResetPasswordInputs> = useCallback(
      async ({ password, passwordHint }, event) => {
        event?.preventDefault();

        clearAllAlerts();
        setIsProcessing(true);

        try {
          await session.memberService.resetPassword(password, passwordToken, passwordHint);
          setSuccessAlert("We have successfully updated your password");
        } catch (e) {
          log.error(e);
          setErrorAlert(parsePWSError(e));
        } finally {
          setIsProcessing(false);
        }
      },
      [clearAllAlerts, passwordToken, session.memberService, setErrorAlert, setSuccessAlert],
    );

    return (
      <section className="component-reset-password">
        <CoastAlertsDisplay alerts={alerts} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <SimpleInput
            placeholder="Enter your new password"
            rules={{
              maxLength: { value: 32, message: "Password can't be longer than 32 characters" },
              minLength: { value: 6, message: "Password can't be shorter that 6 characters" },
              required: FORM_ERRORS.required,
            }}
            error={errors.password}
            register={register}
            label="New Password"
            name="password"
            type="password"
          />

          <SimpleInput
            placeholder="Confirm your new password"
            rules={{
              validate: v => validatePasswordsMatch(watch("password"), v),
              required: FORM_ERRORS.required,
            }}
            error={errors.confirmPassword}
            register={register}
            label="Confirm new Password"
            name="confirmPassword"
            type="password"
          />

          <SimpleInput
            placeholder="Enter your password hint"
            rules={{ validate: v => validatePasswordHintContains(v, watch("password")) }}
            error={errors.passwordHint}
            register={register}
            label="Password Hint"
            name="passwordHint"
            optional={true}
            type="text"
          />

          <UsersButton
            disabled={!!errors.password || !!errors.confirmPassword || isProcessing}
            isProcessing={isProcessing}
            type="submit"
            label="Reset Password"
          >
            Reset Password
          </UsersButton>
        </form>

        <Button click={goBack} className="coast-button-text back-to-login" kind={ButtonKind.CUSTOMIZED}>
          Back To Login
        </Button>
      </section>
    );
  },
);
